import map from 'lodash/map'
import get from 'lodash/get'
import toFinite from 'lodash/toFinite'
import findIndex from 'lodash/findIndex'
import size from 'lodash/size'
import range from 'lodash/range'
import filter from 'lodash/filter'

import { annuity } from '@utils/financeCalculations'
import { getYearFromAge } from '@utils/dateParser'
import { buildDatasetConfig, CHART_NAMES } from './config'
import { resolveJointInflation } from './inflation'
import { ChartTypes, ChartView } from '../index'

// Construct the `datasets` object of the projections
// in the shape required by chartjs
// see: https://www.chartjs.org/docs/latest/general/data-structures.html
// and see: https://www.chartjs.org/docs/latest/charts/line.html
export const buildProjectionDatasets = ({
  forecast,
  participantsQueries,
  colors,
  chartType,
  chartView,
  timeValue,
  showTotalExpense,
  showGrossIncome,
}) => {
  const incomeBreakdownView = showTotalExpense || showGrossIncome
  const joint = chartView === ChartView.joint

  // These 2 functions are the actual expensive calls that can be optimized
  const actualDataPointsForClients = constructDataPointsForForecastsForClients(
    participantsQueries,
    forecast
  )

  // The joint series used to be built without this second argument, so its
  // present value was the annuity default for every couple no matter what
  // either of them had chosen. Which of two rates a joint chart should use is
  // resolved in ./inflation, and named on the chart.
  const actualDataPointsForJointScenario = constructDataPointsForJointScenario(
    forecast,
    { inflation: resolveJointInflation(participantsQueries).rate }
  )

  
  const enabledStates = {
    [`${CHART_NAMES.ACTUAL_FORECAST}_1`]: !(incomeBreakdownView || joint),
    [`${CHART_NAMES.ACTUAL_FORECAST}_2`]: !(incomeBreakdownView || joint),
    [CHART_NAMES.JOINT_FORECAST]: incomeBreakdownView || joint,
    [CHART_NAMES.EXPENSE_FORECAST]: chartType === ChartTypes.income && showTotalExpense,
    [CHART_NAMES.INCOME_FORECAST]: chartType === ChartTypes.income && showGrossIncome
  }
  const config = buildDatasetConfig(colors, enabledStates, {
    chartType
  })

  const datasets = createDatasetsForForecasts(actualDataPointsForClients, actualDataPointsForJointScenario, config, {
    chartType,
    timeValue
  })

  return datasets
}

const createDatasetsForForecasts = (actualDataPointsForClients, actualDataPointsForJointScenario, config, {
  timeValue,
  chartType,
}) => {
  let datasets = {}
  // ChartJS blows up if the labels are identical. It is a rare occurance but cause headaches trying to debug.
  let preferredNames = createUniquePreferredNames(actualDataPointsForClients)
  
  Object.entries(actualDataPointsForClients).forEach(([_clientId, clientDataPoints], idx) => {
    datasets[`${CHART_NAMES.ACTUAL_FORECAST}_${idx + 1}`] = {
      data: clientDataPoints.dataset,
      tickIndexes: [
        0,
        findIndex(clientDataPoints.dataset, { assets: 0 }),
        findIndex(clientDataPoints.dataset, { age: `${clientDataPoints.expectedAge}` }),
        findIndex(clientDataPoints.dataset, { age: `${clientDataPoints.retirementAge}` }),
        size(clientDataPoints) - 1
      ],
      defaultIndex: findIndex(clientDataPoints.dataset, { age: `${clientDataPoints.retirementAge}` }),
      tickType: 'age',
      parsing: {
        xAxisKey: 'year',
        yAxisKey: `${chartType}.${timeValue}`
      },
      label: `${preferredNames[idx]} ${chartType === ChartTypes.income ? 'net income' : 'capital'}`,
    }
  })
  datasets[CHART_NAMES.JOINT_FORECAST] = {
    data: actualDataPointsForJointScenario,
    tickIndexes: [
      0,
      findIndex(actualDataPointsForJointScenario, { assets: 0 }),
      size(actualDataPointsForJointScenario) - 1],
    defaultIndex: 0,
    tickType: 'year',
    parsing: {
      xAxisKey: 'year',
      yAxisKey: `${chartType}.${timeValue}`
    },
    label: `Joint ${chartType === ChartTypes.income ? 'net income' : 'capital'}`,
  }
  datasets[CHART_NAMES.INCOME_FORECAST] = {
    data: actualDataPointsForJointScenario,
    tickIndexes: [
      0,
      findIndex(actualDataPointsForJointScenario, { assets: 0 }),
      size(actualDataPointsForJointScenario) - 1],
    defaultIndex: 0,
    tickType: 'year',
    subtextKey: `monthlytax.${timeValue}`,
    parsing: {
      xAxisKey: 'year',
      yAxisKey: `grossMonthlyIncome.${timeValue}`
    }
  }
  datasets[CHART_NAMES.EXPENSE_FORECAST] = {
    data: actualDataPointsForJointScenario,
    tickIndexes: [
      0,
      findIndex(actualDataPointsForJointScenario, { assets: 0 }),
      size(actualDataPointsForJointScenario) - 1],
    defaultIndex: 0,
    tickType: 'year',
    parsing: {
      xAxisKey: 'year',
      yAxisKey: `totalMonthlyExpenses.${timeValue}`
    }
  }
  return mapDatasetsToChartJsDatasets(datasets, config)
}

function createUniquePreferredNames(actualDataPointsForClients) {
  const preferredNames = [];
  Object.values(actualDataPointsForClients).forEach(clientDataPoints => {
    let preferredName = clientDataPoints.preferredName;
    // Check if the preferredName already exists in the array
    while (preferredNames.includes(preferredName)) {
      // If it does, add a trailing space to make it unique
      preferredName += ' ';
    }
    // Add the unique preferredName to the array
    preferredNames.push(preferredName);
  });
  return preferredNames;
}

const mapDatasetsToChartJsDatasets = (datasets, config) => {
  return Object.entries(datasets).map(([chartName, dataset]) => {
    if (config[chartName].enabled) {
      return {
      ...config[chartName],
      ...dataset,
      }
    } else {
      return false
    }
  }).filter(Boolean)
}

const constructDataPointsForForecastsForClients = (
  participantsQueries,
  jointForecasts
) => {
  return participantsQueries.reduce((datasetObject, participantQuery) => {
    const forecast = jointForecasts.client_forecasts[`client_${participantQuery.participant.id}`]
    const currentAge = participantQuery.participant.currentAge
    const inflation = participantQuery?.scenario?.data?.baseScenario?.assumptions?.inflation || 0.06
    const cashflowEvents = participantQuery?.cashflowEvents?.data || []

    const dataset = map(forecast, (dataPoint) => ({
      assets: get(dataPoint, 'assets'),
      capital: {
        future: get(dataPoint, 'assets'),
        present: annuity(get(dataPoint, 'assets'), currentAge - toFinite(get(dataPoint, 'age')), inflation)
      },
      income: {
        future: get(dataPoint, 'net_monthly_income'),
        present: Math.round(annuity(get(dataPoint, 'net_monthly_income'), currentAge - toFinite(get(dataPoint, 'age')), inflation))
      },
      grossMonthlyIncome: {
        future: get(dataPoint, 'gross_monthly_income'),
        present: Math.round(annuity(get(dataPoint, 'gross_monthly_income'), currentAge - toFinite(get(dataPoint, 'age')), inflation)),
      },
      monthlytax: {
        future: get(dataPoint, 'gross_monthly_income') - get(dataPoint, 'net_monthly_income'),
        present: Math.round(annuity(
          get(dataPoint, 'gross_monthly_income') - get(dataPoint, 'net_monthly_income'), currentAge - toFinite(get(dataPoint, 'age')), inflation
        ))
      },
      totalMonthlyExpenses: {
        future: get(dataPoint, 'total_monthly_expenses'),
        present: Math.round(annuity(get(dataPoint, 'total_monthly_expenses'), currentAge - toFinite(get(dataPoint, 'age')), inflation))
      },
      age: `${get(dataPoint, 'age')}`,
      year: `${getYearFromAge(get(dataPoint, 'age'), currentAge)}`,
      cashflowEvents: buildProjectionCashflowEventsForAge(cashflowEvents, currentAge, get(dataPoint, 'age'), inflation),
      cashflowEventsDrawStatus: get(dataPoint, 'cashflow_events_draw_status'),
      livingAnnuityAtMax: get(dataPoint, 'living_annuity_at_max_withdrawal', false)
    }))
    return {
      ...datasetObject,
      [participantQuery.participant.id]: {
        dataset,
        currentAge,
        retirementAge: participantQuery.scenario.data.baseScenario.retirementAge,
        expectedAge: participantQuery.scenario.data.baseScenario.expectedAge,
        preferredName: participantQuery.participant.preferredName
      }
    }
  }, {})
}

const constructDataPointsForJointScenario = (
  forecast,
  props
) => {
  const inflation = get(props, 'inflation', 0.06)
  const currentYear = forecast?.joint_forecast[0]?.year

  return map(forecast.joint_forecast, (dataPoint) => ({
    assets: get(dataPoint, 'assets'),
    capital: {
      future: get(dataPoint, 'assets'),
      present: annuity(get(dataPoint, 'assets'), currentYear - toFinite(get(dataPoint, 'year')), inflation)
    },
    income: {
      future: get(dataPoint, 'net_monthly_income'),
      present: Math.round(annuity(get(dataPoint, 'net_monthly_income'), currentYear - toFinite(get(dataPoint, 'year')), inflation))
    },
    grossMonthlyIncome: {
      future: get(dataPoint, 'gross_monthly_income'),
      present: Math.round(annuity(get(dataPoint, 'gross_monthly_income'), currentYear - toFinite(get(dataPoint, 'year')), inflation)),
    },
    monthlytax: {
      future: get(dataPoint, 'gross_monthly_income') - get(dataPoint, 'net_monthly_income'),
      present: Math.round(annuity(
        get(dataPoint, 'gross_monthly_income') - get(dataPoint, 'net_monthly_income'), currentYear - toFinite(get(dataPoint, 'year')), inflation
      ))
    },
    totalMonthlyExpenses: {
      future: get(dataPoint, 'total_monthly_expenses'),
      present: Math.round(annuity(get(dataPoint, 'total_monthly_expenses'), currentYear - toFinite(get(dataPoint, 'year')), inflation))
    },
    // True when either spouse's living annuity is drawing at the 17.5%
    // legislated maximum, so the chart can mark the ages where the household's
    // income cannot be lifted any further.
    livingAnnuityAtMax: get(dataPoint, 'living_annuity_at_max_withdrawal', false),
    year: `${get(dataPoint, 'year')}`,
  }))
}
  

const buildProjectionCashflowEventsForAge = (cashflowEvents, currentAge, age, inflation) => {
  const cashflowEventsAtAge = filter(cashflowEvents, (cashflowEvent) => {
    const { start_age, end_age, frequency } = cashflowEvent
    return range(start_age, end_age + 1, frequency).includes(age)
  })

  // Add some extra data here to make it easier for chartjs
  // to work off of cashflowEvents
  return map(cashflowEventsAtAge, (cashflowEvent) => ({
    ...cashflowEvent,
    pointAge: age,
    currentAge,
    inflation: inflation
  }))
}
